import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { from, Observable } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { getCurrentAccount } from "../accounts/accounts.reducer";
import { LANG } from "../app.component";
import { AuthService } from "../auth/auth.service";
import type { PrintCardOrderItemPreviewResult } from "../print-cards/print-cards.interface";
import {
  PrintCardOrder,
  PrintCardOrderListParams,
  PrintCardShipperDefaults,
  PrintCardFulfillmentAdvancePayload,
  PrintCardOrderNameLabelResult,
  PrintCardPrinterSubmitProgressSnapshot,
  UpdatePrintCardOrderShipmentPayload,
  AttachPrintCardShipmentPayload,
  PrintCardUpsPackPlan,
  PrintCardUpsRateQuote,
  PrintCardUpsShopRateQuotesResult,
  SavePrintCardOrderUpsPackingPayload,
  SchedulePrintCardOrderUpsPickupPayload,
  ShipPrintCardOrderUpsPayload,
  UpsShipmentOptions,
  MergePrintCardOrdersPayload,
} from "./print-card-orders.interface";
import { PaginatedData } from "../shared/model/table.interface";

@Injectable({ providedIn: "root" })
export class PrintCardOrdersService {
  private readonly basePath = "/admin/print-card-orders";
  private readonly progressHttp: HttpClient;

  constructor(
    private readonly http: HttpClient,
    httpBackend: HttpBackend,
    private readonly auth: AuthService,
    private readonly store: Store,
    private readonly translate: TranslateService,
  ) {
    this.progressHttp = new HttpClient(httpBackend);
  }

  listOrders(
    params: PrintCardOrderListParams,
  ): Observable<PaginatedData<PrintCardOrder>> {
    const httpParams: Record<string, string> = {
      page: String(params.page),
      limit: String(params.limit),
    };
    if (params.phrase?.trim()) {
      httpParams.phrase = params.phrase.trim();
    }
    return this.http.get<PaginatedData<PrintCardOrder>>(this.basePath, {
      params: httpParams,
    });
  }

  listMergeCandidates(): Observable<PrintCardOrder[]> {
    return this.http
      .get<{ data: PrintCardOrder[] }>(`${this.basePath}/merge-candidates`)
      .pipe(map((response) => response.data ?? []));
  }

  mergeOrders(
    payload: MergePrintCardOrdersPayload,
  ): Observable<PrintCardOrder> {
    return this.http.post<PrintCardOrder>(`${this.basePath}/merge`, payload);
  }

  unmergeOrder(orderId: string): Observable<PrintCardOrder[]> {
    return this.http
      .post<{ data: PrintCardOrder[] }>(
        `${this.basePath}/${encodeURIComponent(orderId)}/unmerge`,
        {},
      )
      .pipe(map((response) => response.data ?? []));
  }

  downloadDrukExpressPricing(): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.basePath}/drukexpress-pricing`, {
        observe: "response",
        responseType: "blob",
      })
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const header = response.headers.get("content-disposition");
          const plainMatch = header
            ? /filename="?([^";]+)"?/i.exec(header)
            : null;
          return {
            blob: response.body ?? new Blob(),
            filename: plainMatch?.[1]?.trim() || "drukexpress-pricing.csv",
          };
        }),
      );
  }

  getOrder(orderId: string): Observable<PrintCardOrder> {
    return this.http.get<PrintCardOrder>(`${this.basePath}/${orderId}`);
  }

  getPrinterSubmitProgress(
    orderId: string,
  ): Observable<PrintCardPrinterSubmitProgressSnapshot> {
    const path = `${this.basePath}/${encodeURIComponent(orderId.trim())}/printer-submit-progress`;
    return from(this.auth.waitForAuthReady()).pipe(
      switchMap(() => this.auth.getToken().pipe(take(1))),
      switchMap((token) =>
        this.store.select(getCurrentAccount).pipe(
          take(1),
          switchMap((currentAccount) => {
            const headers: Record<string, string> = {};
            if (token) {
              headers.Authorization = `Bearer ${token}`;
            }
            if (currentAccount) {
              headers.domain = currentAccount;
            }
            const language = (
              localStorage.getItem(LANG) ||
              this.translate.currentLang ||
              this.translate.defaultLang ||
              "en"
            )
              .trim()
              .toLowerCase()
              .split(/[-_]/)[0];
            if (language === "pl" || language === "es" || language === "en") {
              headers["x-app-language"] = language;
            }
            return this.progressHttp.get<PrintCardPrinterSubmitProgressSnapshot>(
              `${environment.apiUrl}${path}`,
              { headers: new HttpHeaders(headers) },
            );
          }),
        ),
      ),
    );
  }

  getShipperDefaults(orderId?: string): Observable<PrintCardShipperDefaults> {
    const params = orderId?.trim()
      ? { params: { orderId: orderId.trim() } }
      : {};
    return this.http.get<PrintCardShipperDefaults>(
      `${this.basePath}/shipper-defaults`,
      params,
    );
  }

  getUpsShipmentOptions(): Observable<UpsShipmentOptions> {
    return this.http.get<UpsShipmentOptions>(
      `${this.basePath}/ups-shipment-options`,
    );
  }

  getUpsPackPlan(orderId: string): Observable<PrintCardUpsPackPlan> {
    return this.http.get<PrintCardUpsPackPlan>(
      `${this.basePath}/${orderId}/ups-pack-plan`,
    );
  }

  shipWithUps(
    orderId: string,
    payload: ShipPrintCardOrderUpsPayload,
  ): Observable<PrintCardOrder> {
    return this.http.post<PrintCardOrder>(
      `${this.basePath}/${orderId}/ship-with-ups`,
      payload,
    );
  }

  voidUpsShipment(orderId: string): Observable<PrintCardOrder> {
    return this.http.delete<PrintCardOrder>(
      `${this.basePath}/${orderId}/ups-shipment`,
    );
  }

  scheduleUpsPickup(
    orderId: string,
    payload: SchedulePrintCardOrderUpsPickupPayload,
  ): Observable<PrintCardOrder> {
    return this.http.post<PrintCardOrder>(
      `${this.basePath}/${orderId}/ups-pickup`,
      payload,
    );
  }

  cancelUpsPickup(orderId: string): Observable<PrintCardOrder> {
    return this.http.delete<PrintCardOrder>(
      `${this.basePath}/${orderId}/ups-pickup`,
    );
  }

  getUpsRate(
    orderId: string,
    payload: ShipPrintCardOrderUpsPayload,
  ): Observable<PrintCardUpsRateQuote> {
    return this.http.post<PrintCardUpsRateQuote>(
      `${this.basePath}/${orderId}/ups-rate`,
      payload,
    );
  }

  getUpsShopRates(
    orderId: string,
    payload: ShipPrintCardOrderUpsPayload,
  ): Observable<PrintCardUpsShopRateQuotesResult> {
    return this.http.post<PrintCardUpsShopRateQuotesResult>(
      `${this.basePath}/${orderId}/ups-shop-rates`,
      payload,
    );
  }

  attachShipment(
    orderId: string,
    payload: AttachPrintCardShipmentPayload,
  ): Observable<PrintCardOrder> {
    return this.http.post<PrintCardOrder>(
      `${this.basePath}/${orderId}/attach-shipment`,
      payload,
    );
  }

  advanceFulfillment(
    orderId: string,
    payload: PrintCardFulfillmentAdvancePayload,
  ): Observable<PrintCardOrder> {
    return this.http.patch<PrintCardOrder>(
      `${this.basePath}/${orderId}/fulfillment`,
      payload,
    );
  }

  forceSubmitToPrinter(orderId: string): Observable<PrintCardOrder> {
    return this.http.post<PrintCardOrder>(
      `${this.basePath}/${orderId}/submit-to-printer`,
      {},
    );
  }

  updateShipment(
    orderId: string,
    payload: UpdatePrintCardOrderShipmentPayload,
  ): Observable<PrintCardOrder> {
    return this.http.patch<PrintCardOrder>(
      `${this.basePath}/${orderId}/shipment`,
      payload,
    );
  }

  saveUpsPacking(
    orderId: string,
    payload: SavePrintCardOrderUpsPackingPayload,
  ): Observable<PrintCardOrder> {
    return this.http.patch<PrintCardOrder>(
      `${this.basePath}/${orderId}/ups-packing`,
      payload,
    );
  }

  setItemNfcEncoded(
    orderId: string,
    externalOrderId: string,
    encodedCount: number,
  ): Observable<PrintCardOrder> {
    return this.http.patch<PrintCardOrder>(
      `${this.basePath}/${orderId}/items/nfc-encoded`,
      { externalOrderId, encodedCount },
    );
  }

  getItemPdf(orderId: string, itemId: string): Observable<Blob> {
    return this.http.get(
      `${this.basePath}/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId.trim())}/pdf`,
      { responseType: "blob" },
    );
  }

  getItemPreview(
    orderId: string,
    itemId: string,
  ): Observable<PrintCardOrderItemPreviewResult> {
    return this.http.get<PrintCardOrderItemPreviewResult>(
      `${this.basePath}/${encodeURIComponent(orderId)}/items/${encodeURIComponent(itemId.trim())}/preview`,
    );
  }

  getNameLabel(
    orderId: string,
    employeeEmail?: string | null,
  ): Observable<PrintCardOrderNameLabelResult> {
    const params: Record<string, string> = {};
    const email = employeeEmail?.trim();
    if (email) {
      params.employeeEmail = email;
    }
    return this.http.get<PrintCardOrderNameLabelResult>(
      `${this.basePath}/${orderId}/name-label`,
      { params },
    );
  }
}
